import { useState, useEffect } from 'react'
import './Collections.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const EMPTY_CREATE_FORM = { name: '', description: '' }

const Collections = () => {
    const [collections, setCollections]   = useState([])
    const [loading, setLoading]           = useState(true)
    const [selected, setSelected]         = useState(null)   // collection (with populated versions)
    const [detailLoading, setDetailLoading] = useState(false)

    const [showCreate, setShowCreate]     = useState(false)
    const [createForm, setCreateForm]     = useState(EMPTY_CREATE_FORM)
    const [creating, setCreating]         = useState(false)

    const [editMode, setEditMode]         = useState(false)
    const [editForm, setEditForm]         = useState({})
    const [saving, setSaving]             = useState(false)

    // Add version picker
    const [showAddVersion, setShowAddVersion] = useState(false)
    const [products, setProducts]         = useState([])
    const [selectedProductId, setSelectedProductId] = useState('')
    const [productVersions, setProductVersions] = useState([])
    const [selectedVersionId, setSelectedVersionId] = useState('')
    const [loadingVersions, setLoadingVersions] = useState(false)
    const [addingVersion, setAddingVersion] = useState(false)

    const [deletingVersionId, setDeletingVersionId] = useState(null)
    const [deletingCollId, setDeletingCollId]       = useState(null)

    const loadCollections = async () => {
        setLoading(true)
        try {
            const data = await apiFetch('/collection')
            setCollections(data.collections || [])
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { loadCollections() }, [])

    const selectCollection = async (id) => {
        setDetailLoading(true)
        setEditMode(false)
        setShowAddVersion(false)
        try {
            const data = await apiFetch(`/collection/${id}`)
            setSelected(data.collection)
            setEditForm({ name: data.collection.name, description: data.collection.description || '' })
        } catch (err) {
            console.error(err)
        } finally {
            setDetailLoading(false)
        }
    }

    const handleCreate = async (e) => {
        e.preventDefault()
        setCreating(true)
        try {
            const data = await apiFetch('/collection', {
                method: 'POST',
                body: JSON.stringify({ name: createForm.name, description: createForm.description }),
            })
            setCollections(prev => [data.collection, ...prev])
            setCreateForm(EMPTY_CREATE_FORM)
            setShowCreate(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setCreating(false)
        }
    }

    const handleSaveEdit = async (e) => {
        e.preventDefault()
        setSaving(true)
        try {
            const data = await apiFetch(`/collection/${selected._id}`, {
                method: 'PUT',
                body: JSON.stringify({ name: editForm.name, description: editForm.description }),
            })
            setSelected(prev => ({ ...prev, name: data.collection.name, description: data.collection.description }))
            setCollections(prev => prev.map(c => c._id === data.collection._id ? { ...c, ...data.collection } : c))
            setEditMode(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDeleteCollection = async (id) => {
        if (!window.confirm('Supprimer cette collection ?')) return
        setDeletingCollId(id)
        try {
            await apiFetch(`/collection/${id}`, { method: 'DELETE' })
            setCollections(prev => prev.filter(c => c._id !== id))
            setSelected(null)
        } catch (err) {
            alert(err.message)
        } finally {
            setDeletingCollId(null)
        }
    }

    const handleToggleOnline = async () => {
        try {
            const data = await apiFetch(`/collection/${selected._id}`, {
                method: 'PUT',
                body: JSON.stringify({ onlineStatus: !selected.onlineStatus }),
            })
            setSelected(prev => ({ ...prev, onlineStatus: data.collection.onlineStatus }))
            setCollections(prev => prev.map(c => c._id === data.collection._id ? { ...c, onlineStatus: data.collection.onlineStatus } : c))
        } catch (err) {
            alert(err.message)
        }
    }

    // ── Add version flow ──────────────────────────────────────────────────────

    const openAddVersion = async () => {
        setSelectedProductId('')
        setSelectedVersionId('')
        setProductVersions([])
        try {
            const data = await apiFetch('/product')
            setProducts(data.products || [])
        } catch (err) {
            alert(err.message)
            return
        }
        setShowAddVersion(true)
    }

    const handleProductChange = async (e) => {
        const pid = e.target.value
        setSelectedProductId(pid)
        setSelectedVersionId('')
        setProductVersions([])
        if (!pid) return
        setLoadingVersions(true)
        try {
            const data = await apiFetch(`/product/${pid}/versions`)
            setProductVersions(data.versions || [])
        } catch (err) {
            console.error(err)
        } finally {
            setLoadingVersions(false)
        }
    }

    const handleAddVersion = async (e) => {
        e.preventDefault()
        if (!selectedVersionId) return
        setAddingVersion(true)
        try {
            await apiFetch(`/collection/${selected._id}/add-version`, {
                method: 'POST',
                body: JSON.stringify({ versionId: selectedVersionId }),
            })
            // Re-fetch to get populated version
            const data = await apiFetch(`/collection/${selected._id}`)
            setSelected(data.collection)
            setShowAddVersion(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setAddingVersion(false)
        }
    }

    const handleRemoveVersion = async (versionId) => {
        if (!window.confirm('Retirer cette version de la collection ?')) return
        setDeletingVersionId(versionId)
        try {
            await apiFetch(`/collection/${selected._id}/remove-version`, {
                method: 'POST',
                body: JSON.stringify({ versionId }),
            })
            setSelected(prev => ({
                ...prev,
                products: prev.products.filter(v => String(v._id || v) !== String(versionId)),
            }))
        } catch (err) {
            alert(err.message)
        } finally {
            setDeletingVersionId(null)
        }
    }

    // ── DETAIL VIEW ───────────────────────────────────────────────────────────

    if (detailLoading) return <div className="collections-loading">Chargement...</div>

    if (selected) {
        const versions = selected.products || []
        return (
            <div className="collections-detail">
                <div className="collections-detail-header">
                    <button className="collections-back-btn" onClick={() => setSelected(null)}>← Retour</button>
                    <h2 className="collections-detail-title">{selected.name}</h2>
                    <button
                        className="collections-edit-btn"
                        onClick={() => {
                            setEditMode(e => !e)
                            setEditForm({ name: selected.name, description: selected.description || '' })
                        }}
                    >
                        {editMode ? 'Annuler' : 'Modifier'}
                    </button>
                </div>

                {editMode ? (
                    <form className="collections-edit-form" onSubmit={handleSaveEdit}>
                        <div className="collections-field">
                            <label>Nom de la collection</label>
                            <input
                                value={editForm.name}
                                onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="collections-field">
                            <label>Description</label>
                            <textarea
                                value={editForm.description}
                                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                                rows={2}
                            />
                        </div>
                        <div className="collections-form-actions">
                            <button type="button" className="collections-btn-outline" onClick={() => setEditMode(false)}>Annuler</button>
                            <button type="submit" className="collections-btn-primary" disabled={saving}>
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="collections-info-card">
                        {selected.description && (
                            <div className="collections-info-row">
                                <span className="collections-info-label">Description</span>
                                <span>{selected.description}</span>
                            </div>
                        )}
                        <div className="collections-info-row">
                            <span className="collections-info-label">Statut</span>
                            <button
                                className={`collections-toggle ${selected.onlineStatus ? 'online' : 'offline'}`}
                                onClick={handleToggleOnline}
                            >
                                {selected.onlineStatus ? 'En ligne' : 'Hors ligne'}
                            </button>
                        </div>
                    </div>
                )}

                {/* ── VERSIONS ─────────────────────────────────────────────── */}
                <div className="collections-versions-section">
                    <div className="collections-versions-header">
                        <h3>Versions ({versions.length})</h3>
                        {!showAddVersion && (
                            <button className="collections-btn-primary" onClick={openAddVersion}>
                                + Ajouter une version
                            </button>
                        )}
                    </div>

                    {showAddVersion && (
                        <form className="collections-add-version-form" onSubmit={handleAddVersion}>
                            <h4>Ajouter une version</h4>
                            <div className="collections-field">
                                <label>Produit</label>
                                <select value={selectedProductId} onChange={handleProductChange} required>
                                    <option value="">— Choisir un produit —</option>
                                    {products.map(p => <option key={p._id} value={p._id}>{p.title}</option>)}
                                </select>
                            </div>
                            {selectedProductId && (
                                <div className="collections-field">
                                    <label>Version</label>
                                    <select
                                        value={selectedVersionId}
                                        onChange={e => setSelectedVersionId(e.target.value)}
                                        required
                                        disabled={loadingVersions}
                                    >
                                        <option value="">
                                            {loadingVersions ? 'Chargement...' : '— Choisir une version —'}
                                        </option>
                                        {productVersions.map(v => (
                                            <option key={v._id} value={v._id}>
                                                v{v.versionNumber} — {v.color}{v.sizeId?.label ? ` / ${v.sizeId.label}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="collections-form-actions">
                                <button type="button" className="collections-btn-outline" onClick={() => setShowAddVersion(false)}>
                                    Annuler
                                </button>
                                <button
                                    type="submit" className="collections-btn-primary"
                                    disabled={addingVersion || !selectedVersionId}
                                >
                                    {addingVersion ? 'Ajout...' : 'Ajouter'}
                                </button>
                            </div>
                        </form>
                    )}

                    {versions.length === 0 ? (
                        <p className="collections-empty-text">Aucune version dans cette collection.</p>
                    ) : (
                        <div className="collections-versions-list">
                            {versions.map(v => {
                                const vid = v._id || v
                                const isObj = typeof v === 'object' && v !== null && v.versionNumber !== undefined
                                return (
                                    <div key={String(vid)} className="collections-version-card">
                                        <div className="collections-version-info">
                                            {isObj ? (
                                                <>
                                                    <span className="collections-version-product">
                                                        {v.productId?.title || '—'}
                                                    </span>
                                                    <span className="collections-version-number">v{v.versionNumber}</span>
                                                    <span>
                                                        {/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v.color) && (
                                                            <span className="collections-version-color-dot" style={{ background: v.color }} />
                                                        )}
                                                        {v.color}
                                                    </span>
                                                    {v.sizeId?.label && <span>{v.sizeId.label}</span>}
                                                </>
                                            ) : (
                                                <span className="collections-version-number">{String(vid)}</span>
                                            )}
                                        </div>
                                        <button
                                            className="collections-btn-danger"
                                            onClick={() => handleRemoveVersion(String(vid))}
                                            disabled={deletingVersionId === String(vid)}
                                        >
                                            {deletingVersionId === String(vid) ? '...' : 'Retirer'}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                <div className="collections-danger-zone">
                    <button
                        className="collections-btn-danger-outline"
                        onClick={() => handleDeleteCollection(selected._id)}
                        disabled={deletingCollId === selected._id}
                    >
                        {deletingCollId === selected._id ? 'Suppression...' : 'Supprimer cette collection'}
                    </button>
                </div>
            </div>
        )
    }

    // ── LIST VIEW ─────────────────────────────────────────────────────────────

    if (loading) return <div className="collections-loading">Chargement...</div>

    return (
        <div className="collections-page">
            <div className="collections-header">
                <h2>Mes collections</h2>
                <button
                    className="collections-btn-primary"
                    onClick={() => { setShowCreate(s => !s); setCreateForm(EMPTY_CREATE_FORM) }}
                >
                    {showCreate ? 'Annuler' : '+ Nouvelle collection'}
                </button>
            </div>

            {showCreate && (
                <form className="collections-create-form" onSubmit={handleCreate}>
                    <div className="collections-field">
                        <label>Nom de la collection</label>
                        <input
                            value={createForm.name}
                            onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                            required placeholder="Nom"
                        />
                    </div>
                    <div className="collections-field">
                        <label>Description (optionnel)</label>
                        <textarea
                            value={createForm.description}
                            onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                            rows={2} placeholder="Description de la collection"
                        />
                    </div>
                    <div className="collections-form-actions">
                        <button type="submit" className="collections-btn-primary" disabled={creating}>
                            {creating ? 'Création...' : 'Créer'}
                        </button>
                    </div>
                </form>
            )}

            {collections.length === 0 ? (
                <p className="collections-empty-text">Aucune collection. Créez votre première collection !</p>
            ) : (
                <div className="collections-list">
                    {collections.map(c => (
                        <div key={c._id} className="collections-list-item" onClick={() => selectCollection(c._id)}>
                            <div className="collections-list-item-info">
                                <span className="collections-list-item-name">{c.name}</span>
                                <span className="collections-list-item-meta">
                                    {c.products?.length ?? 0} version{c.products?.length !== 1 ? 's' : ''}
                                    {c.description ? ` · ${c.description}` : ''}
                                </span>
                            </div>
                            <span className="collections-list-item-arrow">›</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default Collections
